import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { useHistory, useParams } from 'react-router-dom';

import Layout from 'components/Layout';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Wizard from 'components/UI/Wizard';
import { DealerDetails } from 'components/Dealer/DealerWizard';
import { uiActions } from 'state/modules/UI';
import { userGroupsActions } from 'state/modules/userGroups';
import { buildUrl } from 'utils';
import i18n from 'i18n-js';

const stepperContainerSx = {
  '& .dealer-onboarding-stepper': {
    margin: '0 auto',
    maxWidth: '500px',
    '& .MuiPaper-root.MuiStepper-root.MuiStepper-horizontal': {
      justifyContent: 'center',
    },
  },
};

const DealerOnboarding = () => {
  const history = useHistory();
  const { id } = useParams();
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const successMessage = i18n.t('user_groups.dealer_created');

  const stepsList = [
    {
      position: 1,
      key: 'details',
      label: i18n.t('user_groups.dealer_details'),
    },
  ];

  const {
    loading,
    onboarding: { exitStep, position, exitOnboarding, errors, steps, lastStep },
  } = useSelector((state) => state.userGroups || {});

  const formRef = useRef(null);

  const dispatch = useDispatch();

  const onPrevious = () => {
    dispatch(userGroupsActions.changePosition('prev'));
  };

  const handleNextPosition = () => {
    dispatch(userGroupsActions.changePosition('next'));
  };

  const handleEndOnboarding = (showFeedback = true) => {
    dispatch(userGroupsActions.resetOnboarding());
    if (showFeedback) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    if (steps?.details?.id) {
      return history.push(buildUrl(`/dealers/${steps?.details?.id}`));
    }
    history.push(buildUrl('/dealers'));
  };

  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (exit) {
          handleEndOnboarding(exit);
        } else if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      default:
        break;
    }
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(userGroupsActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(userGroupsActions.fetchUserGroupById(id, 'onboarding'));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (errors && lastStep && (Array.isArray(errors) || Object.keys(errors).length !== 0)) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]); // eslint-disable-line

  useEffect(() => {
    if (exitOnboarding) {
      handleEndOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const getButtonText = () => {
    return position === 1
      ? i18n.t('common.finish')
      : id
        ? i18n.t('wizard.update_and_continue')
        : i18n.t('wizard.create');
  };

  const userGroupsWizard = () => {
    return (
      <Box sx={stepperContainerSx}>
        <Wizard
          mode="create"
          exit={exitStep}
          steps={stepsList}
          loading={loading}
          title={i18n.t('user_groups.create_dealer')}
          position={position}
          handleExit={handleExit}
          onNext={handleNextStep}
          onPrevious={onPrevious}
          onSaveAndExit={onSaveAndExit}
          successMessage={successMessage}
          handleEndOnboarding={handleEndOnboarding}
          showSaveAndExitModal={showSaveAndExitModal}
          customStepperClass="dealer-onboarding-stepper"
          handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
          saveButtonText={getButtonText()}
        >
          {position === 0 && (
            <DealerDetails
              exit={exitStep}
              errors={errors}
              formRef={formRef}
              step={steps.details}
              type={id ? 'update' : 'create'}
              handleNextPosition={handleNextPosition}
            />
          )}
        </Wizard>
      </Box>
    );
  };

  return (
    <Layout title="Dashboard">
      <Container maxwidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={12}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginBottom: 48 }}
            >
              <Grid>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} my={1}>
                    Create Dealer
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>{userGroupsWizard()}</Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default DealerOnboarding;
